import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VoyahCourageSliderEditor = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [targetSlideId, setTargetSlideId] = useState(null);

  const LANGS = ["En", "Ar", "Ku"];

  const resolveUrl = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  // ============================================
  // LOAD SLIDES (3 languages at once)
  // ============================================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VoyahCourageSlider?lang=en`),
          axios.get(`${API_BASE}/api/VoyahCourageSlider?lang=ar`),
          axios.get(`${API_BASE}/api/VoyahCourageSlider?lang=ku`),
        ]);

        // Transform to editable form
        const merged = en.data.map((slide, i) => ({
          id: slide.id,

          // TEXTS
          labelEn: en.data[i].label,
          labelAr: ar.data[i].label,
          labelKu: ku.data[i].label,

          titleEn: en.data[i].title,
          titleAr: ar.data[i].title,
          titleKu: ku.data[i].title,

          buttonEn: en.data[i].button,
          buttonAr: ar.data[i].button,
          buttonKu: ku.data[i].button,

          articleUrl: en.data[i].articleUrl,

          // IMAGE
          image: en.data[i].image,
        }));

        setSlides(merged);
        setLoading(false);
      } catch (err) {
        console.error(err);
      }
    };

    load();
  }, []);

  // ============================================
  // HANDLE TEXT CHANGE
  // ============================================
  const handleChange = (index, field, value) => {
    const copy = [...slides];
    copy[index][field] = value;
    setSlides(copy);
  };

  // ============================================
  // SAVE TEXTS FOR ALL SLIDES
  // ============================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    try {
      for (const s of slides) {
        await axios.put(
          `${API_BASE}/api/VoyahCourageSlider/${s.id}`,
          {
            id: s.id,
            LabelEn: s.labelEn,
            LabelAr: s.labelAr,
            LabelKu: s.labelKu,
            TitleEn: s.titleEn,
            TitleAr: s.titleAr,
            TitleKu: s.titleKu,
            ButtonEn: s.buttonEn,
            ButtonAr: s.buttonAr,
            ButtonKu: s.buttonKu,
            ArticleUrl: s.articleUrl,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      alert("Slides saved successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save.");
    }
  };

  // ============================================
  // DELETE IMAGE
  // ============================================
  const handleDeleteImage = async (slideId, imageUrl) => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_BASE}/api/VoyahCourageSlider/image`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { slideId, url: imageUrl },
      });

      alert("Image deleted!");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Delete failed.");
    }
  };

  // ============================================
  // UPLOAD IMAGE
  // ============================================
  const handleUploadImage = async () => {
    if (!uploadFile) return alert("Choose a file.");

    const token = localStorage.getItem("token");
    const fd = new FormData();

    fd.append("SlideId", targetSlideId);
    fd.append("Image", uploadFile);

    try {
      await axios.put(`${API_BASE}/api/VoyahCourageSlider/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Image updated!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Voyah COURAGE – Slider Editor</h2>

      {/* ============================================ */}
      {/* SLIDES LIST */}
      {/* ============================================ */}
      {slides.map((slide, index) => (
        <div className="card mb-4 shadow-sm" key={slide.id}>
          <div className="card-body">
            <h4 className="mb-3">Slide #{slide.id}</h4>

            {/* MULTI-LANGUAGE TEXT BLOCKS */}
            <div className="row">
              {LANGS.map((lang) => (
                <div className="col-lg-4 mb-4" key={lang}>
                  <div className="border rounded p-3">
                    <h6 className="text-primary mb-3">{lang} version</h6>

                    {/* Label */}
                    <label className="form-label">Label</label>
                    <input
                      className="form-control mb-2"
                      value={slide[`label${lang}`]}
                      onChange={(e) =>
                        handleChange(index, `label${lang}`, e.target.value)
                      }
                    />

                    {/* Title */}
                    <label className="form-label">Title</label>
                    <textarea
                      className="form-control mb-2"
                      rows={2}
                      value={slide[`title${lang}`]}
                      onChange={(e) =>
                        handleChange(index, `title${lang}`, e.target.value)
                      }
                    />

                    {/* Button */}
                    <label className="form-label">Button text</label>
                    <input
                      className="form-control"
                      value={slide[`button${lang}`]}
                      onChange={(e) =>
                        handleChange(index, `button${lang}`, e.target.value)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Article URL */}
            <label className="form-label mt-3">Article URL</label>
            <input
              className="form-control mb-3"
              value={slide.articleUrl}
              onChange={(e) =>
                handleChange(index, "articleUrl", e.target.value)
              }
            />

            {/* IMAGE */}
            <h6 className="mt-4">Slide Image</h6>

            {slide.image ? (
              <img
                src={resolveUrl(slide.image)}
                alt=""
                className="w-100 rounded mb-3"
                style={{ maxHeight: "260px", objectFit: "cover" }}
              />
            ) : (
              <p>No image uploaded</p>
            )}

            <div className="d-flex gap-2">
              <button
                className="btn btn-danger"
                disabled={!slide.image}
                onClick={() => handleDeleteImage(slide.id, slide.image)}
              >
                Delete
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowModal(true);
                  setTargetSlideId(slide.id);
                }}
              >
                Upload / Replace
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* SAVE BUTTON */}
      <div className="text-end">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save All Slides
        </button>
      </div>

      {/* ============================================ */}
      {/* MODAL UPLOAD */}
      {/* ============================================ */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Upload image for Slide #{targetSlideId}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button className="btn btn-primary" onClick={handleUploadImage}>
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
};

export default VoyahCourageSliderEditor;
